import { View, ScrollView, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import Head from "expo-router/head";

export default function PrivacyPolicy() {
  const { colors } = useTheme();

  return (
    <>
      <Head>
        <title>Privacy Policy - RockForce App</title>
      </Head>
      <View style={styles.outerContainer}>
        <ScrollView contentContainerStyle={styles.content}>
          <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <ThemedText style={[styles.title, { color: colors.text }]}>Privacy Policy</ThemedText>
            <ThemedText style={[styles.updated, { color: colors.textSecondary }]}>
              Last Updated: June 5, 2026
            </ThemedText>

            <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>Introduction</ThemedText>
            <ThemedText style={[styles.body, { color: colors.textSecondary }]}>
              RockForce ({'"'}we,{'"'} {'"'}us,{'"'} or {'"'}our{'"'}) is a workforce management platform providing
              payroll, staffing, and operations services. This Privacy Policy describes how we collect, use, and protect
              your personal information when you use our platform and SMS messaging services.
            </ThemedText>

            <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>Information We Collect</ThemedText>
            <ThemedText style={[styles.body, { color: colors.textSecondary }]}>
              We collect the following information to operate our services:{"\n\n"}
              {"\u2022"} Name and contact information (email address, phone number){"\n"}
              {"\u2022"} Employment-related data (position, shift assignments, work schedule){"\n"}
              {"\u2022"} Account credentials{"\n"}
              {"\u2022"} Device information and usage data when you access the platform{"\n"}
              {"\u2022"} SMS opt-in status and messaging preferences
            </ThemedText>

            <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>How We Use Your Information</ThemedText>
            <ThemedText style={[styles.body, { color: colors.textSecondary }]}>
              Your information is used to:{"\n\n"}
              {"\u2022"} Operate and maintain your RockForce account{"\n"}
              {"\u2022"} Send shift invitations and work-related notifications via SMS{"\n"}
              {"\u2022"} Facilitate scheduling, payroll, and workforce operations{"\n"}
              {"\u2022"} Communicate important updates about your account or assignments{"\n"}
              {"\u2022"} Improve and maintain our platform
            </ThemedText>

            <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>No Third-Party Sharing</ThemedText>
            <ThemedText style={[styles.body, { color: colors.textSecondary }]}>
              We do not sell, rent, or share your personal information with third parties for marketing or advertising
              purposes. Your data is only shared with service providers strictly necessary to operate our platform
              (e.g., SMS delivery services) and is never used for purposes unrelated to RockForce.
            </ThemedText>

            <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>SMS Messaging</ThemedText>
            <ThemedText style={[styles.body, { color: colors.textSecondary }]}>
              When you opt in to receive SMS messages from RockForce, we collect and use your mobile phone number solely
              for sending work-related notifications such as shift invitations and scheduling updates. Your phone number
              and opt-in consent are not shared with third parties for their own marketing purposes. You may opt out of
              SMS messaging at any time by replying STOP to any message.
            </ThemedText>

            <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>Data Security</ThemedText>
            <ThemedText style={[styles.body, { color: colors.textSecondary }]}>
              We implement industry-standard security measures to protect your personal information, including
              encryption in transit and at rest, secure authentication, and access controls. While no system is
              completely secure, we take reasonable steps to safeguard your data.
            </ThemedText>

            <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>Data Retention</ThemedText>
            <ThemedText style={[styles.body, { color: colors.textSecondary }]}>
              We retain your personal information for as long as your account is active or as needed to provide our
              services. If you request account deletion, we will remove your personal data within a reasonable
              timeframe, subject to any legal obligations requiring retention.
            </ThemedText>

            <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>Your Rights</ThemedText>
            <ThemedText style={[styles.body, { color: colors.textSecondary }]}>
              You have the right to:{"\n\n"}
              {"\u2022"} Access the personal information we hold about you{"\n"}
              {"\u2022"} Request correction of inaccurate data{"\n"}
              {"\u2022"} Request deletion of your account and data{"\n"}
              {"\u2022"} Opt out of SMS communications at any time
            </ThemedText>

            <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>Contact Us</ThemedText>
            <ThemedText style={[styles.body, { color: colors.textSecondary }]}>
              If you have questions about this Privacy Policy or your personal data, please contact us at
              support@rockforce.com.
            </ThemedText>
          </View>
        </ScrollView>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    alignItems: "center",
  },
  content: {
    padding: 20,
    width: "100%",
    maxWidth: 700,
    alignSelf: "center",
  },
  card: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 4,
  },
  updated: {
    fontSize: 13,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: "700",
    marginTop: 24,
    marginBottom: 8,
  },
  body: {
    fontSize: 15,
    lineHeight: 24,
  },
});
